<template>
  <div class="overlay-card" :class="[`overlay-card--width-${width}`]">
    <div v-if="hasSlot.title" class="overlay-card__header">
      <div
        v-if="hasSlot['title-action-icon']"
        class="overlay-card__title-action"
        data-test-id="overlay-card-title-action-button"
      >
        <Btn @click="$emit('title-action')" color="ghost" size="medium" icon>
          <slot name="title-action-icon" />
        </Btn>
      </div>

      <div class="overlay-card__title" data-test-id="overlay-card-title">
        <slot name="title" />
      </div>

      <ATooltip v-if="hasSlot.tooltip" placement="top">
        <div class="overlay-card__info">
          <InfoIcon16 />
        </div>

        <template #popper>
          <div class="overlay-card__tooltip">
            <slot name="tooltip" />
          </div>
        </template>
      </ATooltip>

      <Btn
        v-if="closeButton"
        @click="$emit('cancel')"
        color="ghost"
        size="medium"
        class="overlay-card__close"
        icon
        data-test-id="overlay-card-close-button"
      >
        <CloseIcon16 />
      </Btn>
    </div>

    <slot />

    <div
      v-if="hasSlot.content"
      class="overlay-card__content"
      :style="`padding: ${contentPadding}px; gap: ${contentGap}px`"
    >
      <slot name="content" />
    </div>

    <div v-if="hasSlot.footer" class="overlay-card__footer">
      <slot name="footer" />
    </div>
  </div>
</template>

<script lang="ts">
import { defineComponent, PropType } from 'vue';
import CloseIcon16 from '@/components/Icons/CloseIcon16.vue';
import InfoIcon16 from '@/components/Icons/InfoIcon16.vue';
import { hasSlotContent } from '@components/slots';

export default defineComponent({
  emits: { 'title-action': null, cancel: null },
  name: 'OverlayCard',

  components: {
    CloseIcon16,
    InfoIcon16,
  },

  props: {
    closeButton: {
      type: Boolean,
      default: true,
      required: false,
    },

    width: {
      type: String as PropType<'auto' | 'm' | 'l' | 'xl'>,
      default: () => 'auto',
    },

    contentPadding: {
      type: Number,
      default: 16,
    },

    contentGap: {
      type: Number,
      default: 16,
    },
  },

  computed: {
    hasSlot() {
      return {
        content: hasSlotContent(this.$slots.content, {}),
        footer: hasSlotContent(this.$slots.footer, {}),
        title: hasSlotContent(this.$slots.title, {}),
        'title-action-icon': hasSlotContent(this.$slots['title-action-icon'], {}),
        tooltip: hasSlotContent(this.$slots.tooltip, {}),
      };
    },
  },
});
</script>

<style lang="scss">
@use 'settings/colors';
@use 'settings/settings';
@use 'mixins/typography';
@use 'mixins/linkstates';

.overlay-card {
  box-shadow: 0px 14px 66px 8px rgba(0, 0, 0, 0.1);
  border: 1px solid colors.$color-grey-200;
  border-radius: settings.$border-radius;
  background-color: colors.$color-white;
  font-size: 14px;
  z-index: 10;
}

.overlay-card__header {
  display: flex;
  align-items: flex-start;
  gap: 8px;
  padding: 16px 16px 8px;
  border-radius: settings.$border-radius settings.$border-radius 0 0;
}

.overlay-card__footer {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 16px;
  border-radius: 0 0 settings.$border-radius settings.$border-radius;
}

// TODO: is there a smarter way?
.overlay-card__title-action {
  margin-right: 4px;
}

.overlay-card__title {
  font-size: 16px;
  line-height: 24px;
  padding-top: 4px; // center the first line with the close button. button is 32px, title is 24px, so 4px is needed
  font-weight: 500;
  color: colors.$color-grey-900;
}

.overlay-card__info {
  width: 16px;
  height: 32px;
  display: flex;
  justify-content: center;
  align-items: center;
  cursor: pointer;
}

.overlay-card__close {
  margin-left: auto;
}

.overlay-card__content {
  position: relative;
  display: flex;
  flex-direction: column;

  // TODO: refactor, this should not be here, this should be a component
  p {
    @include typography.typography-15-normal(24px);
    margin: 0;

    &:not(:last-child) {
      margin-bottom: 4px;
    }

    strong,
    b {
      color: colors.$color-grey-800;
    }

    a {
      @include linkstates.linkstates {
        color: colors.$color-blue-600;
        text-decoration: underline;
        text-underline-offset: 0.1em;
      }
    }
  }
}

.overlay-card__tooltip {
  max-width: 400px;
}

// TODO: refactor, this should not work this way
.overlay-card__name-grid {
  display: grid;
  grid-gap: 8px;
  grid-template-columns: auto min-content;
  align-items: flex-end; // to fit the color button, what does this break now? we need to rethink this.

  // special case because of the label in the inputbox
  .circle-color-picker {
    margin-top: 29px;
  }
}

.overlay-card--width-m {
  width: settings.$overlay-card-width-default;
}

.overlay-card--width-l {
  width: 600px;
}

.overlay-card--width-xl {
  width: 800px;
}
</style>
